import { reportError } from '../monitoring/errorMonitor';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createHttpClient } from '@wix/http-client';

import { Template } from '../domain/entities/template';
import { HttpTemplateRepository } from '../domain/repositories/HttpTemplateRepository';
import { useMonitoring } from './MonitoringContext';
import { useBiLogger } from './BILoggerContext';
import { useInitialState } from './InitialStateContext';
import { useSearch } from '../hooks/useSearch';
import { useSelectedCategory } from '../hooks/useSelectedCategory';
import { useCurrentPage } from '../hooks/useCurrentPage';
import { TEMPLATES_PER_PAGE } from '../consts';

interface TemplatesContextValue {
  templates: Template[];
  totalTemplatesCount: number;
  loading: boolean;
  error: Error | null;
}

type State = {
  templates: Template[];
  totalTemplatesCount: number;
  categoryId?: string;
  criteria?: string;
  offset: number;
  loading: boolean;
  isFullyLoaded: boolean;
  error: Error | null;
};

const TemplatesContext = createContext<TemplatesContextValue>({
  templates: [],
  totalTemplatesCount: 0,
  loading: false,
  error: null,
});

export const TemplatesProvider: FC<React.PropsWithChildren> = ({
  children,
}) => {
  const initialState = useInitialState();
  const monitoring = useMonitoring();
  const biLogger = useBiLogger();
  const { criteria: selectedCriteria, isSearchActive } = useSearch();
  const currentPage = useCurrentPage();

  const selectedCategory = useSelectedCategory();
  const selectedCategoryId = selectedCategory?.categoryId;

  const [state, setState] = useState<State>({
    templates: initialState.templates,
    categoryId: initialState.categoryId,
    criteria: initialState.criteria,
    offset: initialState.templates.length,
    loading: false,
    isFullyLoaded: false,
    error: null,
    totalTemplatesCount: initialState.totalTemplatesCount,
  });

  const loadTemplates = useCallback(
    (categoryId: string, limit: number, offset: number) => {
      const templateRepository = new HttpTemplateRepository(createHttpClient());
      setState({
        ...state,
        error: null,
        loading: true,
      });
      templateRepository
        .loadTemplates({
          categoryId,
          language: initialState.locale,
          limit,
          offset,
          templateBuilders: window.__TEMPLATE_BUILDERS__,
        })
        .then(({ templates, total, offset: loadedOffset }) => {
          setState({
            templates,
            totalTemplatesCount: total,
            categoryId,
            criteria: undefined,
            offset: loadedOffset,
            loading: false,
            isFullyLoaded: templates.length === 0,
            error: null,
          });
          monitoring.stopActiveInteraction();
        })
        .catch((error) => {
          console.error(error);
          reportError(error);
          setState((prevState) => ({ ...prevState, error }));
        })
        .finally(() => {
          setState((prevState) => ({ ...prevState, loading: false }));
        });
    },
    [monitoring, initialState.locale, state],
  );

  const searchTemplates = useCallback(
    (criteria: string, limit: number, offset: number) => {
      const templateRepository = new HttpTemplateRepository(createHttpClient());
      setState({
        ...state,
        error: null,
        loading: true,
      });

      templateRepository
        .searchTemplates({
          query: criteria,
          language: initialState.locale,
          limit,
          offset,
          templateBuilders: window.__TEMPLATE_BUILDERS__,
        })
        .then(({ templates, total, offset: loadedOffset }) => {
          if (offset === 0) {
            biLogger.reportSearch({ criteria, total });
          }

          setState({
            templates,
            totalTemplatesCount: total,
            categoryId: undefined,
            offset: loadedOffset,
            criteria,
            loading: false,
            isFullyLoaded: templates.length === 0,
            error: null,
          });
        })
        .catch((error) => {
          console.error(error);
          reportError(error);
          setState((prevState) => ({ ...prevState, error }));
        })
        .finally(() =>
          setState((prevState) => ({ ...prevState, loading: false })),
        );
    },
    [biLogger, initialState.locale, state],
  );

  // search with pagination
  useEffect(() => {
    const offset =
      currentPage && Number.isInteger(currentPage) && currentPage > 1
        ? (currentPage - 1) * TEMPLATES_PER_PAGE
        : 0;

    if (
      !isSearchActive ||
      (selectedCriteria === state.criteria && offset === state.offset) ||
      state.loading
    ) {
      return;
    }

    searchTemplates(selectedCriteria, TEMPLATES_PER_PAGE, offset);
  }, [selectedCriteria, currentPage]);

  // load templates with pagination
  useEffect(() => {
    const offset =
      currentPage && Number.isInteger(currentPage) && currentPage > 1
        ? (currentPage - 1) * TEMPLATES_PER_PAGE
        : 0;

    if (
      isSearchActive ||
      (selectedCategoryId === state.categoryId && offset === state.offset) ||
      selectedCategoryId === undefined ||
      state.loading
    ) {
      return;
    }

    loadTemplates(selectedCategoryId, TEMPLATES_PER_PAGE, offset);

    return;
  }, [currentPage, selectedCategoryId, isSearchActive]);

  return (
    <TemplatesContext.Provider
      value={{
        templates: state.templates,
        totalTemplatesCount: state.totalTemplatesCount,
        loading: state.loading,
        error: state.error,
      }}
    >
      {children}
    </TemplatesContext.Provider>
  );
};

export const useTemplates = () => {
  const context = useContext(TemplatesContext);
  if (!context) {
    throw new Error('useTemplates must be used within a TemplatesProvider');
  }

  return context;
};
